/**
 * Keyboard height observer for iOS safe-area and keyboard handling
 * Works in iOS Safari, iOS PWA, and Capacitor WebView
 * 
 * Uses visualViewport API when available to compute keyboard height
 * and sets CSS custom property --keyboard for use in styles
 */

let keyboardObserver: (() => void) | null = null;
let isAttached = false;

/**
 * Compute keyboard height from visualViewport
 * Returns height in pixels (0 if keyboard is not visible)
 */
function computeKeyboardHeight(): number {
  if (typeof window === 'undefined' || !window.visualViewport) {
    return 0;
  }

  const viewport = window.visualViewport;
  const windowHeight = window.innerHeight;
  
  // Keyboard height = difference between window height and visual viewport height
  // Also account for viewport offset (iOS Safari can offset viewport when keyboard appears)
  const keyboardHeight = Math.max(
    0,
    windowHeight - viewport.height - (viewport.offsetTop || 0)
  );

  return keyboardHeight;
}

/**
 * Update CSS custom property --keyboard with current keyboard height
 */
function updateKeyboardHeight(): void {
  const keyboardHeight = computeKeyboardHeight();
  document.documentElement.style.setProperty('--keyboard', `${keyboardHeight}px`);
}

/**
 * Attach keyboard observer
 * Should be called once when app mounts (e.g., in AppShell)
 */
export function attachKeyboardObserver(): () => void {
  if (isAttached) {
    if (import.meta.env.DEV) {
      console.warn('[Keyboard] Observer already attached');
    }
    return () => {};
  }

  if (typeof window === 'undefined') {
    return () => {};
  }

  // Initialize keyboard height
  updateKeyboardHeight();

  // Use visualViewport API if available (iOS Safari, modern browsers)
  if (window.visualViewport) {
    const handleResize = () => updateKeyboardHeight();
    const handleScroll = () => updateKeyboardHeight();

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleScroll);
    // Also listen to window resize as fallback
    window.addEventListener('resize', handleResize);

    keyboardObserver = () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      isAttached = false;
    };
  } else {
    // Fallback: use window resize only
    const handleResize = () => updateKeyboardHeight();
    window.addEventListener('resize', handleResize);

    keyboardObserver = () => {
      window.removeEventListener('resize', handleResize);
      isAttached = false;
    };
  }

  isAttached = true;
  return keyboardObserver;
}

/**
 * Detach keyboard observer
 * Should be called when app unmounts
 */
export function detachKeyboardObserver(): void {
  if (keyboardObserver) {
    keyboardObserver();
    keyboardObserver = null;
  }
}
